import React, { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Location } from 'history';

import { isDevelopment } from 'shared/core/constants/environment';
import { useBasepath } from 'router/hooks/use-basepath';
import ReCAPTCHA from 'react-google-recaptcha';
import { RedirectOnlyIfFirstEntry } from 'router/RedirectComponents';
import { addUtmTrackingParamsToUrl } from 'tracking/utm-tracking/utm-tracking';
import { useRouteParam } from 'hooks/use-query/use-query';
import { UtmCampaign, UtmMedium, UtmSource } from 'tracking/utm-tracking/types';
import { Sso } from 'features/authentication/sso/sso';
import { WebviewRedirect } from 'features/authentication/components/webview-redirect/webview-redirect';
import { isMobileWeb } from 'utils/detect-environment';
import { WebviewRegistrationDone } from '../components/webview-registration-done/webview-registration-done';
import { AppNotEnabled } from 'features/mobile-report/components/app-not-enabled/app-not-enabled';
import { PlSwitch, PlRoute } from 'shared/router';
import { AnonymousPermissionsManager } from 'features/authentication/components/anonymous-permissions-manager/anonymous-permissions-manager';
import LoginPage from 'features/authentication/login-page/login-page';
import SignupPage from 'features/authentication/signup/signup-page';
import { ForgotPassword } from 'features/authentication/forgot-password/forgot-password';
import { EmailMfa } from 'features/authentication/mfa/email/email-mfa';
import { EmailMfaBounced } from 'features/authentication/mfa/email/email-mfa-bounced';
import { EmailMfaExpired } from 'features/authentication/mfa/email/email-mfa-expired';
import { CompleteMfaLogin } from 'features/authentication/mfa/complete-login/complete-login';

const recaptchaRoutes: string[] = ['/signin', '/signup', '/forgot-password'];

const appendUtmQueryParams = (nextUrlQueryParam: string, location: Location) => {
    const urlSearchParams = new URLSearchParams(nextUrlQueryParam);
    const utmSourceFromNextUrl = urlSearchParams.get('utm_source');
    const utmMediumFromNextUrl = urlSearchParams.get('utm_medium');
    const utmCampaignFromNextUrl = urlSearchParams.get('utm_campaign');
    const utmContentFromNextUrl = urlSearchParams.get('utm_content');

    if (utmSourceFromNextUrl && utmMediumFromNextUrl && utmCampaignFromNextUrl) {
        return addUtmTrackingParamsToUrl({
            url: location.search,
            utmSource: utmSourceFromNextUrl as UtmSource,
            utmMedium: utmMediumFromNextUrl as UtmMedium,
            utmCampaign: utmCampaignFromNextUrl as UtmCampaign,
            utmContent: utmContentFromNextUrl === null ? undefined : utmContentFromNextUrl,
        });
    }
};

export const AuthRouter: React.FC = () => {
    const { basepath } = useBasepath();
    const defaultPath = `${basepath}/signin`;
    const location = useLocation();
    const history = useHistory();
    const utmSourceQueryParam = useRouteParam('utm_source');
    const nextUrlQueryParam = useRouteParam('next_url');
    const utmParamsWereAdded = useRef<boolean>(false);

    useEffect(() => {
        if (!utmParamsWereAdded.current && !utmSourceQueryParam && nextUrlQueryParam) {
            utmParamsWereAdded.current = true;
            const searchParamsWithUtmTrackingParams = appendUtmQueryParams(
                nextUrlQueryParam,
                location,
            );
            if (searchParamsWithUtmTrackingParams) {
                history.push({
                    pathname: location.pathname,
                    search: searchParamsWithUtmTrackingParams,
                    state: location.state,
                });
            }
        }
    }, [history, location, nextUrlQueryParam, utmSourceQueryParam]);

    return (
        <AnonymousPermissionsManager>
            <>
                <PlSwitch>
                    <PlRoute path={'/signin'} exact>
                        <LoginPage />
                    </PlRoute>
                    <PlRoute path={'/signup'}>
                        <SignupPage />
                    </PlRoute>
                    <PlRoute path={['/signin-sso', '/signup-sso']} exact>
                        <Sso />
                    </PlRoute>
                    {isMobileWeb() && (
                        <PlRoute path={'/app-redirect'} exact>
                            <WebviewRedirect />
                        </PlRoute>
                    )}
                    {isMobileWeb() && (
                        <PlRoute path={'/account-activate-success-mobile'} exact>
                            <WebviewRegistrationDone />
                        </PlRoute>
                    )}
                    {isMobileWeb() && (
                        <PlRoute path={'/not-enabled'} exact>
                            <AppNotEnabled />
                        </PlRoute>
                    )}
                    <PlRoute path={'/forgot-password'} exact>
                        <ForgotPassword />
                    </PlRoute>
                    <PlRoute path={'/mfa/email'} exact>
                        <EmailMfa />
                    </PlRoute>
                    <PlRoute path={'/mfa/email-bounced'} exact>
                        <EmailMfaBounced />
                    </PlRoute>
                    <PlRoute path={'/mfa/email-expired'} exact>
                        <EmailMfaExpired />
                    </PlRoute>
                    <PlRoute path={'/mfa/complete-login'} exact>
                        <CompleteMfaLogin />
                    </PlRoute>
                    <PlRoute path="*" exact>
                        <RedirectOnlyIfFirstEntry path={defaultPath} />
                    </PlRoute>
                </PlSwitch>

                {recaptchaRoutes.includes(location.pathname) && (
                    <ReCAPTCHA
                        badge={isDevelopment ? 'inline' : 'bottomright'}
                        size="invisible"
                        sitekey={process.env.REACT_APP_RECAPTCH_SITE_KEY ?? ''}
                    />
                )}
            </>
        </AnonymousPermissionsManager>
    );
};
